import type { Idioma } from './traducir';

// Fechas, números y moneda. Todo esto lo hace el navegador con `Intl`; acá solo
// se le ponen nombres y se guardan los formateadores.
//
// **Por qué se guardan:** construir un `Intl.DateTimeFormat` es caro comparado
// con usarlo. En una lista de doscientas órdenes, crear uno por fila se nota en
// un celular de gama baja — que es el aparato del mecánico, o sea el caso que
// manda en este producto.

const CACHE = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>();

function guardado<T extends Intl.DateTimeFormat | Intl.NumberFormat>(llave: string, crear: () => T): T {
  let formateador = CACHE.get(llave) as T | undefined;
  if (!formateador) {
    formateador = crear();
    CACHE.set(llave, formateador);
  }
  return formateador;
}

/** Solo el día: `17 sept 2026` / `Sep 17, 2026`. */
export function fecha(valor: Date | string, idioma: Idioma): string {
  const d = typeof valor === 'string' ? new Date(valor) : valor;
  return guardado(
    `fecha:${idioma}`,
    () => new Intl.DateTimeFormat(idioma, { dateStyle: 'medium' }),
  ).format(d);
}

/**
 * Día y hora.
 *
 * El backend guarda todo en UTC —es una regla de CLAUDE.md— y esto lo convierte
 * a la hora de quien mira. Es el único lugar donde debería pasar esa conversión:
 * una fecha convertida antes de tiempo ya no se puede volver a convertir bien.
 */
export function fechaHora(valor: Date | string, idioma: Idioma): string {
  const d = typeof valor === 'string' ? new Date(valor) : valor;
  return guardado(
    `fechaHora:${idioma}`,
    () => new Intl.DateTimeFormat(idioma, { dateStyle: 'medium', timeStyle: 'short' }),
  ).format(d);
}

/** Un número con los separadores del idioma: `1,234.5` o `1.234,5`. */
export function numero(valor: number, idioma: Idioma): string {
  return guardado(`numero:${idioma}`, () => new Intl.NumberFormat(idioma)).format(valor);
}

/**
 * Dinero.
 *
 * La moneda se pasa siempre y no tiene valor por omisión, a propósito: **la
 * moneda es del taller, no del idioma**. Alguien con el navegador en inglés
 * puede estar cobrando en quetzales, y suponerlo al revés sería mostrar cifras
 * equivocadas con toda confianza.
 */
export function dinero(valor: number, moneda: string, idioma: Idioma): string {
  return guardado(
    `dinero:${idioma}:${moneda}`,
    () => new Intl.NumberFormat(idioma, { style: 'currency', currency: moneda }),
  ).format(valor);
}
